import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { parseDateInCST } from '../utils/dateUtils';
import LocationDisplay from '../components/LocationDisplay';
import FavoriteButton from '../components/FavoriteButton';
import ShareButton from '../components/ShareButton';
import './SchoolDistrictsPage.css';
import './SharedCardStyles.css';

interface SchoolEvent { id: string; slug: string; title: string; description: string; district_name: string; event_type: string; event_date: string; end_date?: string; is_all_day: boolean; start_time?: string; end_time?: string; venue_name?: string; registration_required: boolean; registration_url?: string; website_url?: string; city: string; }

const cityToDistrict: { [key: string]: string } = { 'Houston': 'Houston ISD', 'Sugar Land': 'Fort Bend ISD', 'Katy': 'Katy ISD', 'Pearland': 'Pearland ISD', 'The Woodlands': 'Conroe ISD', 'Spring': 'Spring ISD', 'Cypress': 'Cypress-Fairbanks ISD', 'Humble': 'Humble ISD', 'Missouri City': 'Fort Bend ISD', 'League City': 'Clear Creek ISD' };

const SchoolDistrictsPage: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<SchoolEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<string>('Houston ISD');
  const [availableDistricts, setAvailableDistricts] = useState<string[]>([]);

  useEffect(() => { 
    fetchDistricts(); 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { const selectedCity = localStorage.getItem('selectedCity') || 'Houston'; const defaultDistrict = cityToDistrict[selectedCity] || 'Houston ISD'; if (availableDistricts.includes(defaultDistrict)) setSelectedDistrict(defaultDistrict); }, [availableDistricts]);
  useEffect(() => { 
    if (selectedDistrict) fetchEvents(); 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDistrict]);

  const fetchDistricts = async () => {
    try {
      const { data, error} = await supabase.from('school_districts').select('district_name').eq('status', 'active');
      if (error) throw error;
      const uniqueDistricts = Array.from(new Set(data?.map(d => d.district_name) || []));
      // Filter to only show Conroe ISD and Houston ISD
      const filteredDistricts = uniqueDistricts.filter(d => d === 'Conroe ISD' || d === 'Houston ISD').sort();
      setAvailableDistricts(filteredDistricts);
      if (filteredDistricts.length > 0 && !selectedDistrict) setSelectedDistrict(filteredDistricts[0]);
    } catch (err: any) { console.error('Error fetching districts:', err); }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true); setError(null);
      const today = new Date().toISOString().split('T')[0];
      let query = supabase.from('school_districts').select('*').eq('status', 'active').eq('district_name', selectedDistrict);
      const { data, error } = await query.or(`end_date.is.null,end_date.gte.${today}`).gte('event_date', today).order('event_date', { ascending: true });
      if (error) throw error;
      setEvents(data || []);
    } catch (err: any) { setError(err.message || 'Failed to load school district events'); } finally { setLoading(false); }
  };

  const formatDate = (dateString: string): string => parseDateInCST(dateString).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/Chicago' });
  const getEventTypeEmoji = (eventType: string): string => { const emojiMap: { [key: string]: string } = { 'School Holiday': '🎉', 'School Schedule': '⏰', 'School Performance': '🎭', 'School Sports': '⚽', 'School': '📚', 'Holiday': '🎉', 'Early Release': '⏰', 'Parent Event': '👨‍👩‍👧‍👦', 'Performance': '🎭', 'Sports': '⚽' }; return emojiMap[eventType] || '📅'; };
  const getEventTypeColor = (eventType: string): string => { const colorMap: { [key: string]: string } = { 'School Holiday': '#FF6B6B', 'School Schedule': '#4ECDC4', 'School Performance': '#A8E6CF', 'School Sports': '#FFD3B6', 'Holiday': '#FF6B6B', 'Early Release': '#4ECDC4', 'Parent Event': '#95E1D3', 'Performance': '#A8E6CF', 'Sports': '#FFD3B6' }; return colorMap[eventType] || '#E0E0E0'; };
  const handleVisitWebsite = (websiteUrl: string | undefined, registrationUrl: string | undefined) => { const url = registrationUrl || websiteUrl; if (!url) return; window.open(url, '_blank'); };

  const groupEventsByMonth = () => {
    const grouped: { [key: string]: SchoolEvent[] } = {};
    events.forEach(event => { const date = parseDateInCST(event.event_date); const monthYear = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'America/Chicago' }); if (!grouped[monthYear]) grouped[monthYear] = []; grouped[monthYear].push(event); });
    return grouped;
  };

  if (loading && events.length === 0) return <div className="school-districts-container"><div className="loading"><div className="spinner"></div><p>Loading school district calendar...</p></div></div>;
  if (error) return <div className="school-districts-container"><div className="error"><p>😕 Oops! {error}</p><button onClick={fetchEvents} className="retry-btn">Try Again</button></div></div>;

  const groupedEvents = groupEventsByMonth();

  return (
    <div className="school-districts-container">
      <button className="back-button" onClick={() => navigate('/')}>← Back to Home</button>
      <LocationDisplay />
      <div className="school-districts-header"><h1>🏫 School District Calendar</h1><p>Important dates, holidays, and events from your local school district</p></div>
      <div className="district-selector"><label htmlFor="district-select">Select School District:</label><select id="district-select" value={selectedDistrict} onChange={(e) => setSelectedDistrict(e.target.value)} className="district-dropdown">{availableDistricts.map((district) => <option key={district} value={district}>{district}</option>)}</select></div>
      {events.length === 0 ? <div className="no-events"><p>📚 No upcoming events found for {selectedDistrict}. Check back soon!</p></div> : (
        <div className="calendar-view">
          {Object.keys(groupedEvents).map((monthYear) => (
            <div key={monthYear} className="month-section">
              <h2 className="month-header">{monthYear}</h2>
              <div className="events-list">
                {groupedEvents[monthYear].filter(event => event.id && event.id !== 'null' && event.id !== 'undefined').map((event) => (
                  <div key={event.id} className="event-card" style={{ borderLeftColor: getEventTypeColor(event.event_type) }}>
                    <div className="favorite-button-wrapper"><FavoriteButton itemId={event.id} itemType="school_district" size="small" /></div>
                    <div className="event-date-badge"><div className="date-day">{parseDateInCST(event.event_date).getDate()}</div><div className="date-month">{parseDateInCST(event.event_date).toLocaleDateString('en-US', { month: 'short', timeZone: 'America/Chicago' })}</div></div>
                    <div className="event-content">
                      <div className="event-header"><h3 className="event-title"><span className="event-emoji">{getEventTypeEmoji(event.event_type)}</span>{event.title}</h3><span className="event-type-badge">{event.event_type}</span></div>
                      {event.description && <p className="event-description">{event.description}</p>}
                      <div className="event-meta">
                        <span className="meta-item">📅 {formatDate(event.event_date)}{event.end_date && event.end_date !== event.event_date && <> - {formatDate(event.end_date)}</>}</span>
                        {!event.is_all_day && event.start_time && <span className="meta-item">🕐 {event.start_time}{event.end_time && <> - {event.end_time}</>}</span>}
                        {event.venue_name && <span className="meta-item">📍 {event.venue_name}</span>}
                      </div>
                      <div className="event-actions">
                        {(event.registration_url || event.website_url) && <button onClick={() => handleVisitWebsite(event.website_url, event.registration_url)} className="visit-website-btn">{event.registration_required ? '📝 Register' : '🌐 Visit Website'}</button>}
                        <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                          <ShareButton 
                            title={event.title}
                            description={event.description}
                            url={`${window.location.origin}/school-districts`}
                            date={formatDate(event.event_date)}
                            location={event.venue_name ? `${event.venue_name}, ${event.city}` : event.city}
                            size="small"
                            showLabel={false}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SchoolDistrictsPage;
